import { postAppleAuthInit } from "../../api/auth/appleLogin";
import {
  getAppleAuthErrorMessage,
  isAppleAuthDisabledError,
} from "./appleAuthErrors";
import { getAppleAuthConfig } from "./appleAuthConfig";
import { loadAppleSignInSdk } from "./loadAppleSignInSdk";

let appleAuthorizationInFlight = null;

const APPLE_CANCELLATION_ERRORS = new Set([
  "popup_closed_by_user",
  "user_cancelled_authorize",
]);

function extractAppleProfileName(user) {
  const name = user?.name;
  if (!name || typeof name !== "object") {
    return { firstName: undefined, lastName: undefined };
  }

  const firstName =
    typeof name.firstName === "string" && name.firstName.trim()
      ? name.firstName.trim()
      : undefined;
  const lastName =
    typeof name.lastName === "string" && name.lastName.trim()
      ? name.lastName.trim()
      : undefined;

  return { firstName, lastName };
}

function buildInitFailure(status, data) {
  return {
    ok: false,
    status,
    data,
    disabled: isAppleAuthDisabledError(status, data),
    message: getAppleAuthErrorMessage(
      data,
      "Unable to start Apple sign-in. Please try again.",
    ),
  };
}

export function isApplePopupRedirectOriginValid(
  redirectUri,
  openerOrigin = window.location.origin,
) {
  try {
    return new URL(redirectUri).origin === openerOrigin;
  } catch (error) {
    return false;
  }
}

function shouldUseApplePopup(redirectUri) {
  return isApplePopupRedirectOriginValid(redirectUri);
}

/**
 * Start Apple Sign-In:
 * 1) claim a server-side attempt via /auth/apple/init/
 * 2) open Apple (popup when origins match; full-page redirect otherwise)
 * 3) return the authorization payload for completeAppleLogin
 *
 * When origins differ (e.g. local HTTP app + HTTPS Return URL), Apple JS
 * cannot deliver web_message to the opener. In that case usePopup=false
 * and Apple form_posts to /api/auth/apple/callback, which redirects back
 * to the frontend with apple_code / apple_state query params.
 *
 * Does not create a Plaintr session.
 */
export async function authorizeWithApple() {
  if (appleAuthorizationInFlight) {
    return appleAuthorizationInFlight;
  }

  appleAuthorizationInFlight = (async () => {
    const config = getAppleAuthConfig();
    if (!config.isConfigured) {
      return {
        ok: false,
        disabled: true,
        message: "Apple sign-in is not configured for this environment.",
      };
    }

    const { response, data } = await postAppleAuthInit();
    if (!response.ok) {
      return buildInitFailure(response.status, data);
    }

    const state = data?.state;
    const nonce = data?.nonce;
    const clientId = data?.client_id;
    const redirectUri = data?.redirect_uri;

    if (!state || !nonce || !clientId || !redirectUri) {
      return {
        ok: false,
        message: "Apple sign-in is misconfigured. Please try again.",
      };
    }

    const usePopup = shouldUseApplePopup(redirectUri);
    if (usePopup && !isApplePopupRedirectOriginValid(redirectUri)) {
      return {
        ok: false,
        message:
          "Apple sign-in redirect does not match this application origin.",
      };
    }

    const appleAuth = await loadAppleSignInSdk();
    appleAuth.init({
      clientId,
      scope: "name email",
      redirectURI: redirectUri,
      state,
      nonce,
      usePopup,
    });

    if (!usePopup) {
      // Full-page navigation to Apple; callback returns via query params.
      await appleAuth.signIn();
      return {
        ok: false,
        redirected: true,
        message: "Redirecting to Apple sign-in.",
      };
    }

    let appleResponse;
    try {
      appleResponse = await appleAuth.signIn();
    } catch (error) {
      const errorCode = error?.error || error?.message;
      if (APPLE_CANCELLATION_ERRORS.has(errorCode)) {
        return {
          ok: false,
          cancelled: true,
          message: "Apple sign-in was cancelled.",
        };
      }

      return {
        ok: false,
        message: "Apple sign-in was cancelled or failed.",
      };
    }

    const code = appleResponse?.authorization?.code;
    const returnedState = appleResponse?.authorization?.state;
    if (!code) {
      return {
        ok: false,
        message: "Apple did not return a valid authorization code.",
      };
    }

    if (!returnedState || returnedState !== state) {
      return {
        ok: false,
        message: "Apple sign-in state is invalid. Please try again.",
      };
    }

    const { firstName, lastName } = extractAppleProfileName(
      appleResponse?.user,
    );

    return {
      ok: true,
      code,
      state: returnedState,
      redirectUri,
      firstName,
      lastName,
    };
  })();

  try {
    return await appleAuthorizationInFlight;
  } finally {
    appleAuthorizationInFlight = null;
  }
}
